import React, { createContext, useContext, useState, useCallback } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/**
 * Lightweight toast notification system using React Bootstrap Toasts.
 *
 * Usage:
 *   Wrap your app in <ToastProvider> (already done in main.jsx or App.jsx).
 *   Then in any component:
 *
 *     const { addToast } = useToast();
 *     addToast('Community created!', 'success');
 *     addToast('Something went wrong', 'danger');
 */

const ToastContext = createContext(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

let toastId = 0;

export function ToastProvider({ children }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, variant = 'success', duration = 4000) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, variant, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ zIndex: 9999, position: 'fixed' }}
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            bg={toast.variant}
            autohide
            delay={toast.duration}
            onClose={() => removeToast(toast.id)}
          >
            <Toast.Header>
              <strong className="me-auto">
                {toast.variant === 'success' && t('toast.success')}
                {toast.variant === 'danger' && t('toast.error')}
                {toast.variant === 'warning' && t('toast.warning')}
                {toast.variant === 'info' && t('toast.info')}
                {!['success', 'danger', 'warning', 'info'].includes(toast.variant) && t('toast.notice')}
              </strong>
            </Toast.Header>
            <Toast.Body className={toast.variant === 'danger' || toast.variant === 'success' ? 'text-white' : ''}>
              {toast.message}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
}
